import React, { useState, useRef, useEffect } from "react";
import { Modal, Nav, Tab, Table, Pagination } from "react-bootstrap";
import IconAssessment from "assets/icons/AssessmentIcon.svg";
import Spinner from "react-bootstrap/Spinner";
import MentalHealthService from "services/mental-health.service";
import AuthService from 'services/auth.service';
import moment from 'moment';
import DayPickerInput from "react-day-picker/DayPickerInput";
import CalendarIcon from 'assets/icons/IconAssessmentCalendar.svg';
import { parseDate } from 'react-day-picker/moment';
import 'react-day-picker/lib/style.css';
import FilterIcon from 'assets/icons/filterIcon.svg';
import IconArrowDown from "assets/icons/expandAllIcon.svg";

const ModalAssessment = React.memo(({ chat }) => {
  const assessmentTypes = [{
    type: 'DassAssessment',
    name: 'DASS-21'
  },{
    type: 'CbiAssessment',
    name: 'CBI'
  },{
    type: 'PhqNineAssessment',
    name: 'PHQ-9'
  }]
  const defaultMetadata = {total_pages: 1, current_page: 1, per_page: 10, total_count: 0};
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [loader, setLoader] = useState(false);
  const [endDate, setEndDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const isServiceCancelled = useRef(false);
  const [metadata, setMetadata] = useState(defaultMetadata);
  const [currentPage, setCurrentPage] = useState(1);
  const [results, setResults] = useState([]);
  const [assessmentCount, setAssessmentCount] = useState([]);
  const [assessmentType, setAssessmentType] = useState('DassAssessment');
  const [showFilter, setShowFilter] = useState(false);
  const handleCloseFilter = () => setShowFilter(false);
  const handleShowFilter = () => setShowFilter(true);

  useEffect(() => {
    if(currentPage !== 1){
      setCurrentPage(1);
    }else{
      const params = {
        user_id: chat?.user_info?.id,
        start_date: startDate,
        end_date: endDate,
        type: assessmentType,
        page: 1,
        per_page: 10
      }
      getAssessmentsData(params);
    }
    return () => {
      isServiceCancelled.current = true;
    };
  }, [startDate, endDate, assessmentType]); // eslint-disable-line

  useEffect(() => {
    const params = {
      user_id: chat?.user_info?.id,
      start_date: startDate,
      end_date: endDate,
      type: assessmentType,
      page: currentPage,
      per_page: 10
    }
    getAssessmentsData(params);
    return () => {
      isServiceCancelled.current = true;
    };
  }, [currentPage]); // eslint-disable-line

  function getAssessmentsData(params){
    setLoader(true);
    MentalHealthService.getAssessments(params).then(
      (data) => {
        setMetadata(data.metadata ? data.metadata : defaultMetadata);
        setResults(data.assessments ? data.assessments : {})
        setAssessmentCount(data.all_assessment_count ? data.all_assessment_count : [])
      },
      (error) => {
        if (error.response.status === 401) {
          AuthService.logout();
          window.location.replace('/eb/login');
        }
      }
    ).then(() => {
      setLoader(false);
    });
  }

  const DassAssessmentTable = () => {
    if (results && Object.keys(results).length) {
      return (
        <Table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Stress Level</th>
              <th>Anxiety Level</th>
              <th>Depression Level</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(results).map(([date, tests]) => {
              return (<tr key={date}>
                <td className="date-col">{moment(date).format('DD-MM-YYYY')}</td>
                {['stress','anxiety','depression'].map((stress_type, index) => {
                  const assessmentTest = tests.find(item => item.stress_type === stress_type);
                  return <td key={index} className={`assessment-col dass ${assessmentTest?.level}`}>{assessmentTest ? assessmentLevelLabel(assessmentTest.level, assessmentTest.score) : '-'}</td>
                })}
              </tr>)
            })}
          </tbody>
        </Table>
      );
    }
    return <div className="no-result">No result</div>;
  };

  const CbiAssessmentTable = () => {
    if (results && Object.keys(results).length) {
      return (
        <Table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Personal Burnout Level</th>
              <th>Work Burnout Level</th>
              <th>Client Burnout Level</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(results).map(([date, tests]) => {
              return (<tr key={date}>
                <td className="date-col">{moment(date).format('DD-MM-YYYY')}</td>
                {['personal_burnout','work_burnout','client_burnout'].map((stress_type, index) => {
                  const assessmentTest = tests.find(item => item.stress_type === stress_type);
                  return <td key={index} className={`assessment-col cbi ${assessmentTest?.level}`}>{assessmentTest ? assessmentLevelLabel(assessmentTest.level, assessmentTest.score) : '-'}</td>
                })}
              </tr>)
            })}
          </tbody>
        </Table>
      );
    }
    return <div className="no-result">No result</div>;
  };

  const PhqNineAssessmentTable = () => {
    if (results && Object.keys(results).length) {
      return (
        <Table>
          <thead>
            <tr>
              <th>Date</th>
              <th>PHQ-9 Level</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(results).map(([date, tests]) => {
              return (<tr key={date}>
                <td className="date-col">{moment(date).format('DD-MM-YYYY')}</td>
                {tests.map((test, index) => {
                  return <td key={index} className={`assessment-col phq ${test.level}`}>{assessmentLevelLabel(test.level, test.score)}</td>
                })}
              </tr>)
            })}
          </tbody>
        </Table>
      );
    }
    return <div className="no-result">No result</div>;
  };
  
  const FilterModal = () => {  
    const [startDateInput, setStartDateInput] = useState(startDate);
    const [endDateInput, setEndDateInput] = useState(endDate);
    const formatDate = (date) => {
      return moment(date).format('YYYY-MM-DD');
    }
    const handleStartDayChange = (e) => {
      setStartDateInput(formatDate(e))
    }
   
    const handleEndDayChange = (e) => {
      setEndDateInput(formatDate(e))
    }

    const handleSaveFilter = (e) => {
      handleCloseFilter();
      setStartDate(startDateInput);
      setEndDate(endDateInput);
    }

    const handleClearFilter = (e) => {
      handleCloseFilter();
      setStartDateInput(null)
      setEndDateInput(null)
      setStartDate(null);
      setEndDate(null);
    }

    return (
      <Modal className="filter-modal" show={showFilter} onHide={handleCloseFilter} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="date-inputs">
            <div className="d-sm-flex align-items-end w-100">
            <div className="start-date-container">
              <p className="mb-1 text-lightgrey font-size-4">From</p>
              <span className="daypicker-container">
                <img src={ CalendarIcon } alt="calendar-icon" className="calendar-icon" />
                <DayPickerInput
                  formatDate={ formatDate }
                  parseDate={ parseDate }
                  format={ 'yyyy-MM-DD' }
                  value={ startDate }
                  onDayChange={ handleStartDayChange }
                />
              </span>
            </div>
            <span className="d-none d-sm-inline-block mb-2 mx-1 font-size-11 fweight-300">-</span>
            <div className="end-date-container">
              <p className="mb-1 text-lightgrey font-size-4">To</p>
              <span className="daypicker-container">
                <img src={ CalendarIcon } alt="calendar-icon" className="calendar-icon" />
                <DayPickerInput
                  formatDate={ formatDate }
                  parseDate={ parseDate }
                  format={ 'yyyy-MM-DD' }
                  value={ endDate}
                  onDayChange={handleEndDayChange}
                />
              </span>
            </div>
          </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn clear-filter-btn" onClick={handleClearFilter}>
            Clear Filters
          </button>
          <button className="btn save-filter-btn" onClick={handleSaveFilter}>
            Filter
          </button>
        </Modal.Footer>
      </Modal>
    );
  };

  const PaginationBar = () => {
    return (
      <Pagination className="justify-content-end">
        <Pagination.Prev
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={metadata.current_page === 1}
        />
        {[...Array(metadata.total_pages)].map((_, index) => (
          <Pagination.Item
            key={index + 1}
            active={index + 1 === metadata.current_page}
            onClick={() => setCurrentPage(index + 1)}
          >
            {index + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, metadata.total_pages))
          }
          disabled={metadata.current_page === metadata.total_pages}
        />
      </Pagination>
    );
  };

  const tableComponents = {DassAssessmentTable,CbiAssessmentTable,PhqNineAssessmentTable};
  function assessmentLevelLabel(level, score){
    const stressLabel = level.replace(/_risk/g,'').replace (/^[-_]*(.)/, (_, c) => c.toUpperCase()).replace (/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase());
    return `${stressLabel} (Level: ${score})`;
  }

  return (
    <>
      <button className="btn assessment-btn" onClick={handleShow}>
        <div className="header-title">
          <img src={IconAssessment} alt="assessment" className="title-icon"/>
          <div className="title">Assessment</div>
        </div>
        <div><img src={IconArrowDown} alt="icon-arrow-down" /></div>
      </button>
      <Modal show={show} onHide={handleClose} size='lg' className="assessment-modal">
        <Modal.Header closeButton>
          <Modal.Title>Assessments</Modal.Title>
        </Modal.Header>
        <div className="loader conditional-display" displayed={loader.toString()}>
          <Spinner animation="border" className="spinner" />
        </div>
        <Modal.Body className="conditional-display" displayed={(!loader).toString()}>
          <Tab.Container id="assessment-tabs" defaultActiveKey={assessmentType} onSelect={(k) => setAssessmentType(k)}>
            <Nav variant="pills" defaultActiveKey={assessmentType}>
              <button className="btn filter-btn" onClick={handleShowFilter}>
                <img src={FilterIcon} alt="filter" />
              </button>
              <div className="custom-tab-items">
                {assessmentTypes.map((item) => (
                  <Nav.Item key={item.type}><Nav.Link eventKey={item.type}>{item.name} ({assessmentCount.find(c => c.name === item.type)?.count || 0})</Nav.Link></Nav.Item>
                ))}
              </div>
            </Nav>
            <Tab.Content>
              {assessmentTypes.map((item, index) => (
                <Tab.Pane key={item.type} eventKey={item.type}>
                  {tableComponents[item.type+'Table'] ? React.createElement(tableComponents[item.type+'Table'], { key: index }) : <div className="no-result">No result</div>}
                </Tab.Pane>
              ))}
            </Tab.Content>
            </Tab.Container>
            {metadata.total_count > 0 && <PaginationBar />}
          </Modal.Body>
        </Modal>
      <FilterModal />
    </>
  );
});

export default ModalAssessment;